import { SET_OPEN_LOGIN_DIALOG } from '@/store/actions.type'

// Mirrors backend com.legacypf.domain.model.SessionFlag.
export const SESSION_FLAG = {
  ACTIVE: 'ACTIVE',
  IDENTITY_VERIFICATION_REQUIRED: 'IDENTITY_VERIFICATION_REQUIRED',
  MIGRATION_REQUIRED: 'MIGRATION_REQUIRED',
  TERMS_ACKNOWLEDGEMENT_REQUIRED: 'TERMS_ACKNOWLEDGEMENT_REQUIRED'
}

// Internal only - nothing outside this file imports either by name (confirmed by grep); both
// feed ALLOWED_PATHS_BY_FLAG below, which isPathAllowed actually reads.
const QUARANTINE_ALLOWED_PATHS = ['/member/identity-verification', '/termsOfService']
const TERMS_JAIL_ALLOWED_PATHS = ['/accept-terms', '/termsOfService']

const ALLOWED_PATHS_BY_FLAG = {
  [SESSION_FLAG.IDENTITY_VERIFICATION_REQUIRED]: QUARANTINE_ALLOWED_PATHS,
  [SESSION_FLAG.MIGRATION_REQUIRED]: QUARANTINE_ALLOWED_PATHS,
  [SESSION_FLAG.TERMS_ACKNOWLEDGEMENT_REQUIRED]: TERMS_JAIL_ALLOWED_PATHS
}

/**
 * Whitelist check for the active session-flag jail.
 * A flag with no entry (ACTIVE, or none) is not this guard's concern - allow.
 */
export const isPathAllowed = (path, sessionFlag) => {
  const allowedPaths = ALLOWED_PATHS_BY_FLAG[sessionFlag]
  if (!allowedPaths) {
    return true
  }
  return allowedPaths.includes(path)
}

// The reverse direction of isPathAllowed above: a page that exists ONLY to serve one specific
// jail (never meant to be reached by an ACTIVE session, and never by no session at all) must
// only render when the current session's own flag is exactly the one that page belongs to.
// requiresAuth (routeMember.js) only ever checked "does a token exist" - true for an expired-but-
// still-present cookie, or for a fully ACTIVE member navigating here directly by URL, neither of
// which should ever see these two screens.
const JAIL_ONLY_PATH_REQUIRED_FLAGS = {
  '/member/identity-verification': [SESSION_FLAG.IDENTITY_VERIFICATION_REQUIRED, SESSION_FLAG.MIGRATION_REQUIRED],
  '/accept-terms': [SESSION_FLAG.TERMS_ACKNOWLEDGEMENT_REQUIRED]
}

/**
 * A path with no entry here isn't jail-only - allow (matches isPathAllowed's own convention).
 * A `sessionFlag` of `null`/`undefined` (no token, or an undecodable one) never matches any real
 * flag in the list below, so an unauthenticated visit is correctly rejected too - not just an
 * ACTIVE one.
 */
export const isJailOnlyPageAllowed = (path, sessionFlag) => {
  const requiredFlags = JAIL_ONLY_PATH_REQUIRED_FLAGS[path]
  if (!requiredFlags) {
    return true
  }
  return requiredFlags.includes(sessionFlag)
}

/**
 * Session-purge helper run on a routing violation. Redirect/flash-message display stay the
 * caller's responsibility, so this stays a pure, easily-mocked unit.
 */
export const handleSessionViolation = ({ jwtService, store }) => {
  jwtService.destroyAll()
  store.dispatch(SET_OPEN_LOGIN_DIALOG, true)
}

const QUARANTINE_VIOLATION_MESSAGE = 'ログアウトしました。本人確認を完了してください。'
const TERMS_JAIL_VIOLATION_MESSAGE = 'ログアウトしました。利用規約への同意を完了してください。'

const VIOLATION_MESSAGE_BY_FLAG = {
  [SESSION_FLAG.IDENTITY_VERIFICATION_REQUIRED]: QUARANTINE_VIOLATION_MESSAGE,
  [SESSION_FLAG.MIGRATION_REQUIRED]: QUARANTINE_VIOLATION_MESSAGE,
  [SESSION_FLAG.TERMS_ACKNOWLEDGEMENT_REQUIRED]: TERMS_JAIL_VIOLATION_MESSAGE
}

/**
 * The flash notification text for a routing-violation redirect, selected by which jail
 * the (now-purged) session was in.
 */
export const getSessionViolationMessage = (sessionFlag) => VIOLATION_MESSAGE_BY_FLAG[sessionFlag]

const JAIL_LEAVE_WARNING_BY_FLAG = {
  [SESSION_FLAG.IDENTITY_VERIFICATION_REQUIRED]: '本人確認を完了してください。未完了のまま別画面に移動するとログアウトします。',
  [SESSION_FLAG.MIGRATION_REQUIRED]: '本人確認を完了してください。未完了のまま別画面に移動するとログアウトします。',
  [SESSION_FLAG.TERMS_ACKNOWLEDGEMENT_REQUIRED]: '利用規約への同意を完了してください。未完了のまま別画面に移動するとログアウトします。'
}

/**
 * The confirm-dialog text shown before an in-app navigation out of a jail logs the member out.
 */
export const getJailLeaveWarningMessage = (sessionFlag) => JAIL_LEAVE_WARNING_BY_FLAG[sessionFlag]

// Shown in the global login dialog when a registration link's own identifier already
// collides with an existing account.
export const ACCOUNT_EXISTS_FLASH_MESSAGE = 'ご入力いただいた情報は、すでに会員登録されています。ログインしてご利用ください。'

// Shown when refresh.service.js gives up on refreshing a dead access/refresh token pair (e.g.
// after a server-side JWT secret rotation invalidates every existing session at once) and force-
// logs the user out locally.
export const REFRESH_FAILED_FLASH_MESSAGE = 'ログアウトしました。再度ログインしてください。'
